import { useState, useCallback } from "react";
import { saveEmployee, updateEmployee, fileUpload } from "../../../api";
import { validateEmployeeForm, generateEmployeePayload } from "./employeeFormDefaults";

/**
 * Custom hook encapsulating the entire employee submission workflow:
 * validation, SSN formatting, API dispatch, file uploads, notifications, and navigation.
 *
 * @param {Object} options
 * @param {string|number|null} options.employeeId - Current employee ID (truthy for update).
 * @param {Object|null} options.editData - Stored original metadata for update payloads.
 * @param {boolean} options.isCloning - Whether cloning is enabled.
 * @param {Array} options.cloneCorps - Target corporations for cloning.
 * @param {Object} options.defaultValues - Fallback initial form values for reset.
 * @param {React.MutableRefObject<string>} options.submitActionRef - "new" or "close".
 * @param {Function} options.enqueueSnackbar - Notistack snackbar trigger.
 * @param {Function} options.onSuccess - Parent callback when save/update succeeds.
 * @param {Function} options.onClose - Modal close handler.
 * @param {Function} options.setFormValues - Local state setter for formValues.
 * @param {Function} options.setEditData - Local state setter for editData.
 * @returns {{ actionLoading: boolean, handleEmployeeSubmit: Function }}
 */
export const useEmployeeSubmit = ({
  employeeId,
  editData,
  isCloning,
  cloneCorps,
  defaultValues,
  submitActionRef,
  enqueueSnackbar,
  onSuccess,
  onClose,
  setFormValues,
  setEditData,
}) => {
  const [actionLoading, setActionLoading] = useState(false);

  const handleEmployeeSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      // 1. Centralized Form Validation
      const errorMsg = validateEmployeeForm(values);
      if (errorMsg) {
        enqueueSnackbar(errorMsg, { variant: "error" });
        setSubmitting(false);
        return;
      }

      // 2. SSN Validation and Formatting
      // If SSN came back masked from the server (e.g. XXX-XX-1234), preserve it.
      const isMaskedSSN = Boolean(employeeId) && /^X{3}-X{2}-\d{4}$/.test(values.ssn);
      const ssnDigits = values.ssn ? values.ssn.replace(/X/g, "").replace(/\D/g, "") : "";

      if (!isMaskedSSN && ssnDigits.length > 0 && ssnDigits.length !== 9) {
        enqueueSnackbar("SSN must be 9 digits", { variant: "error" });
        setSubmitting(false);
        return;
      }

      const formattedSSN = isMaskedSSN
        ? values.ssn
        : ssnDigits.length > 0
          ? `${ssnDigits.slice(0, 3)}-${ssnDigits.slice(3, 5)}-${ssnDigits.slice(5)}`
          : "";

      try {
        setActionLoading(true);

        // 3. Payload Construction & API Dispatch
        const payload = generateEmployeePayload(
          { ...values, ssn: formattedSSN },
          editData,
          employeeId,
          isCloning,
          cloneCorps
        );

        const apiCall = employeeId ? updateEmployee : saveEmployee;
        const response = await apiCall(payload);

        if (response?.StatusCode === 200) {
          const newEmpId =
            employeeId ||
            response.ID ||
            response.EMPID ||
            (response.Data && response.Data.ID);

          // 4. Batch Attachment Uploads if present
          if (newEmpId && values.attachments && values.attachments.length > 0) {
            const filesPayload = values.attachments.map((file) => ({
              FileName: file.name,
              FileContent: file.base64,
              Length: file.size,
              DocumentType: file.type,
            }));

            const uploadPayload = {
              CorporationID: values.corporation,
              RefID: newEmpId,
              Type: "Employee Document",
              Files: filesPayload,
            };
            await fileUpload(uploadPayload);
          }

          enqueueSnackbar(response?.Status || "Employee saved successfully!", { variant: "success" });

          // 5. Post-Submission Navigation & Cleanup
          setTimeout(() => {
            if (onSuccess) {
              const fullName = [
                values.firstName,
                values.middleName,
                values.lastName,
              ]
                .filter(Boolean)
                .join(" ")
                .trim();

              const successPayload = {
                ...values,
                ...response,
                EmployeeID: newEmpId,
              };

              onSuccess({ payload: successPayload, fullName });
            }

            if (submitActionRef.current === "new") {
              resetForm({ values: defaultValues });
              setFormValues(defaultValues);
              setEditData(null);
            } else {
              onClose();
            }
          }, 1000);
        } else {
          const action = employeeId ? "update" : "save";
          enqueueSnackbar(
            response?.Status || `Failed to ${action} employee`,
            { variant: "error" }
          );
        }
      } catch (err) {
        enqueueSnackbar(err.message || "An unexpected error occurred while saving employee.", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
        setActionLoading(false);
      }
    },
    [
      employeeId,
      editData,
      isCloning,
      cloneCorps,
      defaultValues,
      submitActionRef,
      enqueueSnackbar,
      onSuccess,
      onClose,
      setFormValues,
      setEditData,
    ]
  );

  return { actionLoading, handleEmployeeSubmit };
};
